import { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { reduxForm, Field, Form, FieldArray } from 'redux-form';

import { TextField, SelectField } from 'common/components/forms';
import BirthDateFormFields from 'common/components/forms/BirthDateFormFields';
import { Layout, Title, FormError } from 'common/components/layout';
import {
  convertDayMonthYearToDate,
  isDayMonthYearADate
} from 'common/helpers/dateHelper';
import { getDefaultCountryCode, getMessage } from 'common/helpers/miscHelper';
import { convertStringToInteger } from 'common/helpers/numberHelper';
import { phoneFormatForDB, phoneFormat } from 'common/helpers/phoneHelper';
import SubMenu from 'profile/common/SubMenu';
import { getCustomer, editCustomer } from 'redux/customer/actions';
import { asyncValidateEmail } from 'redux/customer/helpers';

import CountryCityStateFormFields from './components/CountryCityStateFormFields';
import EmailFormFields from './components/EmailFormFields';
import PhoneFormFields from './components/PhoneFormFields';
import { validateEdit } from './validators';

class AccountEdit extends Component {
  componentDidMount() {
    const { props } = this;
    props.getCustomer();
  }

  handleSubmit = (form) => {
    const { props } = this;
    const data = transformData(form);
    const successFunction = () => {
      props.history.push('/profile/account');
    };
    props.editCustomer(data, successFunction);
  };

  render() {
    const { settings, customer, form } = this.props;
    const { handleSubmit, change } = this.props;
    if (!settings.data || !customer.data) {
      return (
        <Layout title="Edit Account Information" loading>
          <Title>Your Account</Title>
          <SubMenu />
        </Layout>
      );
    }
    return (
      <Layout title="Edit Account Information">
        <Title>Your Account</Title>
        <SubMenu />
        <div className="c7-account__information">
          <h2>Edit account information</h2>

          <Form
            onSubmit={handleSubmit(this.handleSubmit)}
            className="c7-form"
            method="post"
          >
            <FormError formName={form} />
            <BirthDateFormFields
              name="birthDate"
              countryCode={getDefaultCountryCode(settings)}
            />
            <div className="c7-form__group">
              <Field
                name="firstName"
                label="First Name"
                autocomplete="given-name"
                component={TextField}
                required
              />
              <Field
                name="lastName"
                label="Last Name"
                autocomplete="family-name"
                component={TextField}
                required
              />
            </div>
            <CountryCityStateFormFields
              countryCode={customer.data.countryCode}
              change={change}
            />
            <FieldArray name="emails" component={EmailFormFields} />
            <Field
              name="emailMarketingStatus"
              component={SelectField}
              optionArray={emailMarketingStatusOptionArray()}
              label="Subscribe To Email Marketing?"
            />
            <div
              dangerouslySetInnerHTML={{
                __html: getMessage('Email Marketing Message', settings)
              }}
            />
            <FieldArray name="phones" component={PhoneFormFields} />
            <div className="c7-form__button-row">
              <Link to="/profile/account" className="c7-button-alt">
                Cancel
              </Link>
              <button type="submit" className="c7-button">
                Update Information
              </button>
            </div>
          </Form>
        </div>
      </Layout>
    );
  }
}

const emailMarketingStatusOptionArray = () => {
  const optionArray = [
    { value: '', option: '' },
    { value: 'Subscribed', option: 'Subscribed' },
    { value: 'Unsubscribed', option: 'Unsubscribed' }
  ];
  return optionArray;
};

const transformData = (values) => {
  const emails = [];
  if (values.emails) {
    values.emails.forEach((email) => {
      if (email.email) {
        emails.push({ email: email.email });
      }
    });
  }
  const phones = [];
  if (values.phones) {
    values.phones.forEach((phone) => {
      if (phone.phone) {
        if (values.countryCode) {
          const newPhone = phoneFormatForDB(phone.phone, values.countryCode);
          phones.push({ phone: newPhone });
        } else {
          const newPhone = phone.phone;
          phones.push({ phone: newPhone });
        }
      }
    });
  }
  const data = {
    firstName: values.firstName,
    lastName: values.lastName,
    city: values.city,
    stateCode: values.stateCode,
    countryCode: values.countryCode,
    emails,
    phones,
    emailMarketingStatus:
      values.emailMarketingStatus === '' ? null : values.emailMarketingStatus
  };
  const { birthDay, birthMonth, birthYear } = values;
  if (isDayMonthYearADate(birthDay, birthMonth, birthYear)) {
    data.birthDate = convertDayMonthYearToDate(birthDay, birthMonth, birthYear);
  }
  return data;
};

const validate = (values, ownProps) => {
  const data = transformData(values);
  data.birthDay = values.birthDay && convertStringToInteger(values.birthDay);
  data.birthMonth =
    values.birthMonth && convertStringToInteger(values.birthMonth);
  data.birthYear = values.birthYear && convertStringToInteger(values.birthYear);
  if (ownProps.settings.data) {
    const errors = validateEdit(data, ownProps.settings);
    return errors;
  }
  return {};
};

const mapStateToProps = (state) => {
  const formValues = state.customer.data;
  if (formValues) {
    if (formValues.birthDate) {
      formValues.birthDay = formValues.birthDate.substring(8, 10);
      formValues.birthMonth = formValues.birthDate.substring(5, 7);
      formValues.birthYear = formValues.birthDate.substring(0, 4);
    }
    formValues.phones.forEach((phone) => {
      const newPhone = phone;
      newPhone.phone = phoneFormat(phone.phone, formValues.countryCode);
    });
  }
  return {
    initialValues: formValues,
    settings: state.setting,
    customer: state.customer
  };
};

const shouldAsyncValidate = (params) => {
  if (params.trigger === 'blur') {
    const blurredField =
      params.blurredField && params.blurredField.substring(0, 6);
    if (blurredField && blurredField === 'emails') {
      return true;
    }
  }
  return false;
};

const asyncValidate = (values, dispatch, props, currentField) => {
  const index = currentField.replace(/(^.*\[|\].*$)/g, '');
  const { email } = values.emails[index];
  return asyncValidateEmail(email, index).then((errors) => {
    if (errors) {
      throw errors;
    }
  });
};

AccountEdit = reduxForm({
  form: 'accountEdit',
  enableReinitialize: true,
  onSubmitFail: (err, dispatch) => {
    dispatch({ type: 'ADD_FORM_ERROR', payload: err });
  },
  onSubmitSuccess: (result, dispatch) => {
    dispatch({ type: 'REMOVE_FORM_ERROR' });
  },
  validate,
  asyncValidate,
  shouldAsyncValidate
})(AccountEdit);

export default connect(mapStateToProps, { getCustomer, editCustomer })(
  AccountEdit
);
